import React, { useRef, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PanoramicViewer = ({ location, viewAngle, zoom, onViewChange, onZoomChange, onHotspotClick }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHtmlTour, setIsHtmlTour] = useState(false);

  // Detect if the URL is an HTML page or a direct image
  useEffect(() => {
    const url = location?.panoramaUrl || '';
    const isHtml = url?.endsWith('.html') || url?.includes('tour.html') || url?.includes('?');
    setIsHtmlTour(isHtml);
    setImageLoaded(false);
  }, [location?.panoramaUrl, location?.id]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e?.clientX, y: e?.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const deltaX = e?.clientX - dragStart?.x;
    const deltaY = e?.clientY - dragStart?.y;

    const newYaw = viewAngle?.yaw + deltaX * 0.2;
    const newPitch = Math.max(-90, Math.min(90, viewAngle?.pitch + deltaY * 0.2));

    onViewChange({ yaw: newYaw, pitch: newPitch });
    setDragStart({ x: e?.clientX, y: e?.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (e?.touches?.length === 1) {
      setIsDragging(true);
      setDragStart({ x: e?.touches?.[0]?.clientX, y: e?.touches?.[0]?.clientY });
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e?.touches?.length !== 1) return;

    const deltaX = e?.touches?.[0]?.clientX - dragStart?.x;
    const deltaY = e?.touches?.[0]?.clientY - dragStart?.y;

    const newYaw = viewAngle?.yaw + deltaX * 0.2;
    const newPitch = Math.max(-90, Math.min(90, viewAngle?.pitch + deltaY * 0.2));

    onViewChange({ yaw: newYaw, pitch: newPitch });
    setDragStart({ x: e?.touches?.[0]?.clientX, y: e?.touches?.[0]?.clientY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    onZoomChange(Math.min(zoom + 0.2, 3));
  };

  const handleZoomOut = () => {
    onZoomChange(Math.max(zoom - 0.2, 0.5));
  };

  const handleResetView = () => {
    onViewChange({ yaw: 0, pitch: 0 });
    onZoomChange(1);
  };

  const calculateHotspotPosition = (hotspot) => {
    const yawDiff = hotspot?.position?.yaw - viewAngle?.yaw;
    const pitchDiff = hotspot?.position?.pitch - viewAngle?.pitch;

    const normalizedYaw = ((yawDiff + 180) % 360) - 180;

    if (Math.abs(normalizedYaw) > 90) {
      return null;
    }

    const x = 50 + (normalizedYaw / 90) * 40;
    const y = 50 - (pitchDiff / 90) * 40;

    return { x, y };
  };

  // Render HTML-based 360 tour in iframe
  if (isHtmlTour) {
    return (
      <div
        ref={containerRef}
        className="relative w-full h-[60vh] md:h-[70vh] lg:h-[80vh] bg-muted overflow-hidden"
      >
        <iframe
          src={location?.panoramaUrl}
          title={location?.panoramaAlt || `360° Campus Tour - ${location?.name}`}
          className="absolute inset-0 w-full h-full border-0"
          allow="accelerometer; gyroscope; fullscreen"
          onLoad={() => setImageLoaded(true)}
        />
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading interactive tour...</p>
            </div>
          </div>
        )}
        <div className="absolute top-4 left-4 bg-popover/90 backdrop-blur-sm border border-border rounded-lg px-4 py-2 shadow-lg z-20">
          <p className="text-sm font-medium text-foreground">Interactive 360° Tour</p>
          <p className="text-xs text-muted-foreground mt-1">Use controls within the tour to navigate</p>
        </div>
      </div>
    );
  }

  // Render image-based panorama viewer (original implementation)
  return (
    <div
      ref={containerRef}
      className="relative w-full h-[60vh] md:h-[70vh] lg:h-[80vh] bg-muted overflow-hidden cursor-move select-none"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <img
        src={location?.panoramaUrl}
        alt={location?.panoramaAlt}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-200"
        style={{
          transform: `scale(${zoom}) translate(${-viewAngle?.yaw * 0.5}px, ${viewAngle?.pitch * 0.5}px)`,
          transformOrigin: 'center center'
        }}
        onLoad={() => setImageLoaded(true)}
        draggable={false}
      />
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading panorama...</p>
          </div>
        </div>
      )}
      {imageLoaded && location?.hotspots?.map((hotspot) => {
        const position = calculateHotspotPosition(hotspot);
        if (!position) return null;

        return (
          <button
            key={hotspot?.id}
            className="absolute w-12 h-12 md:w-14 md:h-14 -translate-x-1/2 -translate-y-1/2 group"
            style={{ left: `${position?.x}%`, top: `${position?.y}%` }}
            onClick={(e) => {
              e?.stopPropagation();
              onHotspotClick(hotspot);
            }}
            aria-label={`Explore ${hotspot?.name}`}
          >
            <div className="relative w-full h-full">
              <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-75" />
              <div className="relative w-full h-full bg-primary rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Icon name={hotspot?.icon} size={20} color="white" />
              </div>
            </div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 bg-popover border border-border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              <p className="text-xs font-medium text-foreground">{hotspot?.name}</p>
            </div>
          </button>
        );
      })}
      <div className="absolute top-4 right-4 flex flex-col gap-2">
        <Button
          variant="default"
          size="icon"
          onClick={handleZoomIn}
          aria-label="Zoom in"
          className="bg-card/90 hover:bg-card border border-border backdrop-blur-sm"
        >
          <Icon name="ZoomIn" size={20} color="var(--color-foreground)" />
        </Button>
        <Button
          variant="default"
          size="icon"
          onClick={handleZoomOut}
          aria-label="Zoom out"
          className="bg-card/90 hover:bg-card border border-border backdrop-blur-sm"
        >
          <Icon name="ZoomOut" size={20} color="var(--color-foreground)" />
        </Button>
        <Button
          variant="default"
          size="icon"
          onClick={handleResetView}
          aria-label="Reset view"
          className="bg-card/90 hover:bg-card border border-border backdrop-blur-sm"
        >
          <Icon name="Maximize2" size={20} color="var(--color-foreground)" />
        </Button>
      </div>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-card/90 backdrop-blur-sm border border-border rounded-lg shadow-lg">
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Icon name="MousePointer2" size={16} color="var(--color-muted-foreground)" />
            <span className="text-muted-foreground hidden md:inline">Drag to look around</span>
          </div>
          <div className="w-px h-4 bg-border" />
          <div className="flex items-center gap-2">
            <Icon name="Hand" size={16} color="var(--color-muted-foreground)" />
            <span className="text-muted-foreground hidden md:inline">Click hotspots to explore</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanoramicViewer;